import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { StepperContextService } from '../../../../../../services/stepper-context.service';
import { StepperAnswersService } from '../../../../../../services/stepper-answers.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { SharedDropdownModule } from '../../../../../../../../shared/shared-dropdown.module';
import { Subject, takeUntil } from 'rxjs';
import { AddressValidationService } from '../../../../../../services/address-validation.service';
import { PreApprovalSteps } from '../../../../../../enums/pre-approval-steps.enum';
import { StepSection } from '../../../../../../enums/step-section.enum';
import { PropertySteps } from '../../../../../../enums/property-steps.enum';
import { PurchasePriceInputs } from '../purchase-price/purchase-price.component';
import { NgxCurrencyModule } from "ngx-currency";
import { MatSliderModule } from '@angular/material/slider';
import { StatedIncomeInputs } from '../stated-income/stated-income.component';

export enum DownPaymentInputs {
  DownPayment = 'Down Payment',
  Percentage = 'Percentage',
  PercentageSlider = 'Percentage Slider'
}

@Component({
  selector: 'lgv-down-payment',
  standalone: true,
  imports: [CommonModule, SharedDropdownModule,
    MatDividerModule, MatButtonModule, MatInputModule,
    NgxCurrencyModule, MatSliderModule,
    ReactiveFormsModule, FormsModule
  ],
  templateUrl: './down-payment.component.html',
  styleUrls: ['./down-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownPaymentComponent {
  purchasePrice = Number(this.answerService.getAnswer(PreApprovalSteps.PurchasePrice)[PurchasePriceInputs.PurchasePrice])
  statedIncome = Number(this.answerService.getAnswer(PreApprovalSteps.StatedIncome)[StatedIncomeInputs.Income])
  monthlyIncome = this.statedIncome / 12
  DownPaymentInputs = DownPaymentInputs
  prepopulated = false
  formGroup = this.fb.group({
    [DownPaymentInputs.Percentage]: [10, [Validators.min(10)]],
    [DownPaymentInputs.PercentageSlider]: [10],
    [DownPaymentInputs.DownPayment]: [this.purchasePrice * 0.10, [Validators.required, Validators.pattern(/^[0-9',]*$/), Validators.min(this.purchasePrice * 0.1)]],
  }, {
    validators: [this.downPaymentRatioValidator(this.purchasePrice, this.monthlyIncome)]
  });
  onDestroy$ = new Subject();
  isLoading = false

  @Output() stepChange = new EventEmitter<any>();
  @Output() sectionChange = new EventEmitter<any>();

  currencyOptions = {
    prefix: '',
    suffix: '',
    thousands: ',',
    decimal: '.',
    precision: 0,
    allowNegative: false
  };

  private updating = false;

  constructor(
    public contextService: StepperContextService,
    public answerService: StepperAnswersService,
    public addressService: AddressValidationService,
    private fb: FormBuilder,
  ) {
    this.formGroup.get(DownPaymentInputs.Percentage)?.valueChanges.subscribe((percent: number) => {
      if (this.updating) return;
      percent = this.clamp(percent);
      this.updating = true;
      this.formGroup.patchValue({
        [DownPaymentInputs.PercentageSlider]: percent,
        [DownPaymentInputs.DownPayment]: (this.purchasePrice * percent) / 100
      }, { emitEvent: false });
      this.updating = false;
    });

    // Sync: Slider → Input + Down Payment
    this.formGroup.get(DownPaymentInputs.PercentageSlider)?.valueChanges.subscribe((percent: number) => {
      if (this.updating) return;
      percent = this.clamp(percent);
      this.updating = true;
      this.formGroup.patchValue({
        [DownPaymentInputs.Percentage]: percent,
        [DownPaymentInputs.DownPayment]: (this.purchasePrice * percent) / 100
      }, { emitEvent: false });
      this.updating = false;
    });

    // Sync: Down Payment → Input + Slider
    this.formGroup.get(DownPaymentInputs.DownPayment)?.valueChanges.subscribe((amount: number) => {
      if (this.updating) return;
      amount = Math.min(Math.max(amount ?? 0, this.purchasePrice * 0.10), this.purchasePrice); // min 10%
      const percent = (amount / this.purchasePrice) * 100;
      this.updating = true;
      this.formGroup.patchValue({
        [DownPaymentInputs.Percentage]: +percent.toFixed(2),
        [DownPaymentInputs.PercentageSlider]: +percent.toFixed(2)
      }, { emitEvent: false });
      this.updating = false;
    });
  }

  private clamp(value: number): number {
    if (value == null || isNaN(value)) return 10;
    return Math.max(10, Math.min(value, 100));
  }

  ngAfterViewInit() {
    console.log('purchase', this.purchasePrice)
    let currentAnswers = this.answerService.getAnswer(PreApprovalSteps.DownPayment)
    if (currentAnswers && Object.keys(currentAnswers).length > 0) {
      this.prepopulated = true
      this.formGroup.setValue({
        [DownPaymentInputs.DownPayment]: currentAnswers[DownPaymentInputs.DownPayment] as number ?? this.purchasePrice * 0.10,
        [DownPaymentInputs.Percentage]: currentAnswers[DownPaymentInputs.Percentage] as number ?? 10,
        [DownPaymentInputs.PercentageSlider]: currentAnswers[DownPaymentInputs.PercentageSlider] as number ?? 10,
      });
    }
  }

  downPaymentRatioValidator(purchasePrice: number, monthlyIncome: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const downPayment = control.get(DownPaymentInputs.DownPayment)?.value;

      if (downPayment == null || isNaN(downPayment)) return null;

      const monthlyPayment = (purchasePrice - downPayment) / 360;

      return monthlyPayment >= monthlyIncome
        ? { downPaymentTooLow: true }
        : null;
    };
  }

  onNext() {
    this.answerService.setAnswer(PreApprovalSteps.DownPayment,
      {
        [DownPaymentInputs.DownPayment]: this.formGroup.get(DownPaymentInputs.DownPayment).value
      })
    this.goToStep(PreApprovalSteps.CreditCheck)
  }

  onBack() {
    if (!this.formGroup.invalid) {
      this.answerService.setAnswer(PreApprovalSteps.DownPayment,
        {
          [DownPaymentInputs.DownPayment]: this.formGroup.get(DownPaymentInputs.DownPayment).value
        })
    }
    this.goToStep(PreApprovalSteps.PurchasePrice)
  }

  goToSection(section, step) {
    this.sectionChange.emit([section, step]);
  }

  goToStep(step) {
    this.stepChange.emit(step);
  }

  isNextDisabled() {
    return this.formGroup.invalid || this.isLoading
  }

  ngOnDestroy(): void {
    this.onDestroy$.complete();
    this.onDestroy$.unsubscribe();
  }
}
